import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, MapPin, Cookie, ArrowUp } from 'lucide-react';
import Logo from './Logo';
import { company, legalPaths } from '../data/company';
import { openCookieSettings } from '../lib/consent';

const Footer: React.FC = () => {
  const year = new Date().getFullYear();

  return (
    <footer className="relative overflow-hidden border-t border-white/10 bg-ink-900">
      <div className="bloom -bottom-40 left-1/2 h-[420px] w-[620px] -translate-x-1/2 bg-peri-500/10" />

      <div className="relative mx-auto max-w-edge px-6 py-16 lg:px-10 lg:py-20">
        <div className="grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Logo size={38} />
            <p className="mt-6 max-w-sm text-[15px] leading-relaxed text-paper/55">
              A Rome-based studio designing, engineering and advising on mobile products —
              from the first sketch to the release after next.
            </p>
            <a
              href={`mailto:${company.email}`}
              className="mt-7 inline-flex items-center gap-2.5 text-[15px] text-paper transition-colors hover:text-peri-300"
            >
              <Mail size={15} strokeWidth={1.6} className="text-peri-400" />
              {company.email}
            </a>
          </div>

          <div className="lg:col-span-3">
            <p className="label">Studio</p>
            <address className="mt-5 flex gap-3 not-italic text-[14.5px] leading-relaxed text-paper/55">
              <MapPin size={15} strokeWidth={1.6} className="mt-1 shrink-0 text-peri-400" />
              <span>
                {company.legalName}
                <br />
                {company.address.street}
                <br />
                {company.address.postalCode} {company.address.city}
                <br />
                {company.address.country}
              </span>
            </address>
          </div>

          <div className="lg:col-span-2">
            <p className="label">Sections</p>
            <ul className="mt-5 space-y-3 text-[14.5px]">
              {[
                { href: '/#studio', label: 'Studio' },
                { href: '/#services', label: 'Services' },
                { href: '/#approach', label: 'Approach' },
                { href: '/#contact', label: 'Contact' },
              ].map((item) => (
                <li key={item.href}>
                  <a href={item.href} className="text-paper/55 transition-colors hover:text-paper">
                    {item.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-2">
            <p className="label">Legal</p>
            <ul className="mt-5 space-y-3 text-[14.5px]">
              <li>
                <Link to={legalPaths.privacy} className="text-paper/55 transition-colors hover:text-paper">
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link to={legalPaths.cookies} className="text-paper/55 transition-colors hover:text-paper">
                  Cookie Policy
                </Link>
              </li>
              <li>
                <Link to={legalPaths.terms} className="text-paper/55 transition-colors hover:text-paper">
                  Terms of Use
                </Link>
              </li>
              <li>
                <button
                  type="button"
                  onClick={openCookieSettings}
                  className="inline-flex items-center gap-2 text-left text-paper/55 transition-colors hover:text-paper"
                >
                  <Cookie size={14} strokeWidth={1.6} />
                  Cookie settings
                </button>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-16 flex flex-col gap-5 border-t border-white/10 pt-8 sm:flex-row sm:items-center sm:justify-between">
          <p className="font-mono text-[10.5px] uppercase tracking-label text-paper/55">
            © {year} {company.legalName} · All rights reserved
          </p>
          <div className="flex items-center gap-6">
            <p className="font-mono text-[10.5px] uppercase tracking-label text-paper/55">
              Rome · Italy
            </p>
            <button
              type="button"
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-white/12 text-paper/60 transition-colors hover:border-peri-400/60 hover:text-paper"
              aria-label="Back to top"
            >
              <ArrowUp size={15} strokeWidth={1.6} />
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
